/**
 * État du polling des agents IBM i, pour le bandeau de l'écran de synchronisation.
 *
 * Remplace « Connecté à DB2 via relay HTTP » (17/09/2026) : le relais est banni, ce qui compte
 * est que l'agent de chaque établissement vienne chercher son travail (long polling, toutes les
 * 25 s au plus). Module pur, sans React : testé côté backend (`__tests__/etatPolling.test.js`).
 */

/** Même seuil que la bascule côté serveur (`utils/basculeTransport.js#AGE_POLL_MAX_S`). */
export const AGE_POLL_MAX_S = 120;
/** Cadence du long polling de l'agent (durée d'une attente côté serveur). */
export const CADENCE_POLL_S = 25;

export interface EtablissementPolling {
  code: string;
  agent_mode: string;
  agent_status: { agent_version: string | null; last_poll_at: string | null } | null;
}

export interface LignePolling { code: string; texte: string; ok: boolean }

export interface EtatPolling {
  niveau: 'ok' | 'attention' | 'inconnu';
  titre: string;
  detail: string;
  lignes: LignePolling[];
  /** Au moins un établissement en pull dont l'agent est vu récemment : une synchro peut partir. */
  lectureDb2Possible: boolean;
}

export const dureeLisible = (secondes: number): string => {
  const s = Math.max(0, Math.round(secondes));
  if (s < 120) return `${s} s`;
  if (s < 2 * 3600) return `${Math.floor(s / 60)} min`;
  if (s < 48 * 3600) return `${Math.floor(s / 3600)} h`;
  return `${Math.floor(s / 86400)} j`;
};

export const etatPolling = (etabs: EtablissementPolling[], maintenant: Date = new Date()): EtatPolling => {
  if (etabs.length === 0) {
    return { niveau: 'inconnu', titre: 'Aucun établissement IBM i', detail: '', lignes: [], lectureDb2Possible: false };
  }
  let heriteEnService = false;
  let silencieux = false;
  let enPull = 0;
  let ageMin: number | null = null;

  const lignes = etabs.map((e): LignePolling => {
    if (e.agent_mode !== 'pull') {
      heriteEnService = true;
      return { code: e.code, texte: `${e.code} — transport hérité (WebSocket), à basculer en pull`, ok: false };
    }
    enPull += 1;
    const dernier = e.agent_status?.last_poll_at ? Date.parse(e.agent_status.last_poll_at) : NaN;
    if (Number.isNaN(dernier)) {
      silencieux = true;
      return { code: e.code, texte: `${e.code} — aucun poll reçu`, ok: false };
    }
    const age = (maintenant.getTime() - dernier) / 1000;
    if (age > AGE_POLL_MAX_S) {
      silencieux = true;
      return { code: e.code, texte: `${e.code} — aucun poll depuis ${dureeLisible(age)} (attendu toutes les ${CADENCE_POLL_S} s)`, ok: false };
    }
    ageMin = ageMin === null ? age : Math.min(ageMin, age);
    const version = e.agent_status?.agent_version ? ` · agent ${e.agent_status.agent_version}` : '';
    return { code: e.code, texte: `${e.code} — dernier poll il y a ${dureeLisible(age)}${version}`, ok: true };
  });

  const lectureDb2Possible = ageMin !== null;
  const detail = enPull > 0 && ageMin !== null
    ? `${enPull} établissement${enPull > 1 ? 's' : ''} en pull · dernier poll il y a ${dureeLisible(ageMin)}`
    : '';
  if (heriteEnService) {
    return { niveau: 'attention', titre: 'Transport hérité encore en service', detail, lignes, lectureDb2Possible };
  }
  if (silencieux) {
    return { niveau: 'attention', titre: 'Agent IBM i silencieux', detail, lignes, lectureDb2Possible };
  }
  return { niveau: 'ok', titre: 'Agents IBM i en écoute (long polling)', detail, lignes, lectureDb2Possible };
};
